// Shared presentational primitives for the Module 06 office screens (§4).
// Everything here is dumb: no fetching, no permission logic, so each piece is
// unit-testable and the ten screens stay consistent (one status pill, one
// breadcrumb, one honest money cell, one "waiting on Module NN" note).

import { enumLabel } from "../../../../lib/enum-label";
import { useContext, type ReactNode } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { costCellKind, formatMinor, type CostPresence, type MoneyBearing } from "../lib/cost";
import { fillRatio, formatMeasure } from "../lib/measure";
import { RefLabel as RegisterRefLabel } from "../../../../components/ref-label";
import { useRefName, type RefKind } from "../../../../lib/ref-names";

// ---------------------------------------------------------------------------
// Status pills
// ---------------------------------------------------------------------------

type Tone = "positive" | "neutral" | "muted" | "warning" | "danger";

/**
 * Tone map across every module-06 vocabulary that appears on a §4 screen:
 * stock-unit status (§2.3), lot qc_status (§2.2), transfer status (§2.7),
 * cycle-count + count-line status (§2.11), adjustment-request status (§2.12).
 * Grouped by MEANING so quarantined/rejected always read amber/red regardless
 * of which entity the value came from.
 */
const TONE: Readonly<Record<string, Tone>> = {
  // stock units
  empty: "muted",
  filled: "positive",
  in_use: "positive",
  in_transit: "neutral",
  quarantined: "warning",
  consumed: "muted",
  shipped: "muted",
  retired: "muted",
  // lots
  pending: "neutral",
  released: "positive",
  rejected: "danger",
  // transfers
  draft: "neutral",
  dispatched: "neutral",
  partially_received: "warning",
  received: "positive",
  cancelled: "muted",
  // counts + lines
  in_progress: "neutral",
  review: "warning",
  pending_approval: "warning",
  posted: "positive",
  counted: "neutral",
  recount_requested: "warning",
  accepted: "positive",
};

/**
 * One pill for every §2 vocabulary this area renders. `scope` selects the i18n
 * sub-namespace so "released" can read differently for a lot than for a
 * reservation without the caller knowing.
 */
export function InventoryStatus({
  status,
  scope = "status",
}: {
  status: string;
  scope?: "status" | "qc" | "transfer" | "count" | "line" | "adjustmentRequest";
}) {
  const { t } = useTranslation();
  const tone: Tone = TONE[status] ?? "neutral";
  return (
    <span className={`status-badge status-${tone}`} data-status={status}>
      {enumLabel(t, `inventory.vocab.${scope}`, status)}
    </span>
  );
}

/** §4.1/§4.2: the amber quarantine badge on stock the §7 grants let you see. */
export function QuarantineBadge() {
  const { t } = useTranslation();
  return (
    <span className="status-badge status-warning" data-status="quarantined">
      {t("inventory.vocab.qc.quarantined")}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Location breadcrumb
// ---------------------------------------------------------------------------

/**
 * §4.1/§4.2 location breadcrumb: the server sends the materialized path
 * `WH1/A/A-03`; the spec renders it `WH1 › A › A-03`. A null path means the
 * unit is not stamped anywhere — say so, never render an empty crumb.
 */
export function LocationPath({ path }: { path: string | null | undefined }) {
  const { t } = useTranslation();
  if (path === null || path === undefined || path.length === 0) {
    return <span className="loc-path loc-path-none">{t("inventory.common.noLocation")}</span>;
  }
  const parts = path.split("/").filter((p) => p.length > 0);
  return (
    <span className="loc-path" title={path}>
      {parts.map((part, index) => (
        <span key={`${part}-${String(index)}`}>
          {index > 0 ? (
            <span className="loc-sep" aria-hidden="true">
              {" "}
              ›{" "}
            </span>
          ) : null}
          {part}
        </span>
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Remaining / capacity
// ---------------------------------------------------------------------------

/** §4.1 result card: a remaining/capacity bar + the exact numeric. */
export function RemainingBar({
  remaining,
  capacity,
  uom,
}: {
  remaining: string | null | undefined;
  capacity: string | null | undefined;
  uom: string | null | undefined;
}) {
  const { t, i18n } = useTranslation();
  const ratio = fillRatio(remaining, capacity);
  const value = formatMeasure(remaining, { locale: i18n.language });
  const cap = formatMeasure(capacity, { locale: i18n.language });
  return (
    <span className="remaining">
      {ratio === undefined ? null : (
        <span
          className="remaining-track"
          role="img"
          aria-label={t("inventory.common.remainingOfCapacity", {
            remaining: value,
            capacity: cap,
            uom: uom ?? "",
          })}
        >
          <span className="remaining-fill" style={{ width: `${String(ratio * 100)}%` }} />
        </span>
      )}
      <span className="remaining-text">
        {ratio === undefined
          ? `${value}${uom !== null && uom !== undefined ? ` ${uom}` : ""}`
          : t("inventory.common.remainingOfCapacity", {
              remaining: value,
              capacity: cap,
              uom: uom ?? "",
            })}
      </span>
    </span>
  );
}

/**
 * §4.2 remaining/capacity donut. Pure SVG (no chart dependency — doctrine 4:
 * nothing new gets added to the bundle for a ring). Falls back to the numeric
 * when there is no capacity to divide by.
 */
export function RemainingDonut({
  remaining,
  capacity,
  uom,
}: {
  remaining: string | null | undefined;
  capacity: string | null | undefined;
  uom: string | null | undefined;
}) {
  const { t, i18n } = useTranslation();
  const ratio = fillRatio(remaining, capacity);
  const value = formatMeasure(remaining, { locale: i18n.language });
  const cap = formatMeasure(capacity, { locale: i18n.language });
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const label =
    ratio === undefined
      ? `${value} ${uom ?? ""}`.trim()
      : t("inventory.common.remainingOfCapacity", {
          remaining: value,
          capacity: cap,
          uom: uom ?? "",
        });

  return (
    <figure className="donut" aria-label={label}>
      {ratio === undefined ? null : (
        <svg viewBox="0 0 100 100" className="donut-svg" role="presentation" aria-hidden="true">
          <circle className="donut-track" cx="50" cy="50" r={radius} />
          <circle
            className="donut-fill"
            cx="50"
            cy="50"
            r={radius}
            strokeDasharray={`${String(circumference * ratio)} ${String(circumference)}`}
          />
        </svg>
      )}
      <figcaption className="donut-caption">
        <span className="donut-value">
          {value} {uom ?? ""}
        </span>
        {ratio === undefined ? (
          <span className="donut-sub">{t("inventory.unitCard.noCapacity")}</span>
        ) : (
          <span className="donut-sub">
            {t("inventory.unitCard.ofCapacity", { capacity: cap, uom: uom ?? "" })}
          </span>
        )}
      </figcaption>
    </figure>
  );
}

// ---------------------------------------------------------------------------
// Money (§7 presence-based redaction)
// ---------------------------------------------------------------------------

/**
 * One money cell inside a column that is ALREADY known to be worth rendering
 * (see `costPresence` — a page with no visible values renders no column at all).
 * The three facts stay distinct: a value, a withheld value, an unvalued row.
 */
export function MoneyCell<T extends MoneyBearing>({
  row,
  valueOf,
  currencyOf,
}: {
  row: T;
  valueOf: (row: T) => number | null | undefined;
  currencyOf: (row: T) => string | null | undefined;
}) {
  const { t, i18n } = useTranslation();
  const kind = costCellKind(row, valueOf);
  if (kind === "withheld") {
    return (
      <span
        className="money money-withheld"
        data-testid="money-cell"
        data-kind="withheld"
        title={t("inventory.money.withheldHint")}
      >
        {t("inventory.money.withheld")}
      </span>
    );
  }
  if (kind === "unvalued") {
    return (
      <span className="money money-unvalued" data-testid="money-cell" data-kind="unvalued">
        {t("inventory.money.unvalued")}
      </span>
    );
  }
  const value = valueOf(row);
  const currency = currencyOf(row);
  if (value === null || value === undefined || currency === null || currency === undefined) {
    return (
      <span className="money money-unvalued" data-testid="money-cell" data-kind="unvalued">
        {t("inventory.money.unvalued")}
      </span>
    );
  }
  return (
    <span className="money" data-testid="money-cell" data-kind="visible" data-minor={String(value)}>
      {formatMinor(value, currency, i18n.language)}
    </span>
  );
}

/**
 * The banner that replaces a money COLUMN when nothing on the page is visible.
 * "Withheld" and "unvalued" are different facts (§7) and a recall reviewer must
 * never read a missing column as "no money at stake".
 */
export function MoneyColumnNote({ presence }: { presence: CostPresence }) {
  const { t } = useTranslation();
  if (presence === "visible") return null;
  return (
    <p className="money-note" role="note">
      {presence === "withheld"
        ? t("inventory.money.columnWithheld")
        : t("inventory.money.columnUnvalued")}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Honest degradation
// ---------------------------------------------------------------------------

/**
 * The one way this area renders a term/section an unbound module owns. Never a
 * 0, never an empty list (spec 06 §3.6 amendment, D-047: a fabricated 0 reads
 * as "no POs inbound" and a planner acts on it).
 *
 * The COPY is a capability sentence, not a module number: a KazKabel planner
 * cannot tell whether "Waiting on Module 10" is a bug, a licence or next month,
 * but "Purchasing is not switched on yet — inbound POs are not counted here"
 * says exactly what is missing and what it does to the number. The module
 * number survives in `data-owner` for support only: a tooltip reading
 * "Module 09" is build talk on the glass (P3-UX, D-425). An owner this
 * dictionary does not know reads "Not available".
 */
export function WaitingOnModule({
  owner,
  ownerCode,
}: {
  /** HUMAN copy from the wire (`"procurement (Module 10)"`): support text only,
   * shown as the tooltip when no code came with it and never parsed. */
  owner?: string | undefined;
  /** The machine-readable `owner_module_code` — preferred whenever present. */
  ownerCode?: string | undefined;
}) {
  const { t } = useTranslation();
  // The capability copy is keyed by MODULE NUMBER, which the wire carries as
  // `owner_module_code` (contracts `OwnerModuleCode`). `owner` is English human
  // copy for support and logs and is never parsed: a `\d{2}` lifted out of it
  // mis-keyed on any sentence holding another two-digit number and fell back to
  // the generic line on any rewording (found by M06-S11, 2026-08-02). The
  // parse survived one release as a shim for an API that predated the code;
  // every server that names an owner has sent both since, so it is gone
  // (H-1 WP-D, 2026-10-05) and a missing code reads as an unnamed owner.
  const code = ownerCode !== undefined && ownerCode.length > 0 ? ownerCode : undefined;
  const known = code !== undefined && code.length > 0;
  return (
    <span
      className="waiting-on"
      data-testid="waiting-on"
      data-owner={code ?? "unknown"}
      data-owner-text={owner}
    >
      {known
        ? t(`inventory.waiting.${code}`, {
            defaultValue: t("inventory.common.waitingOn"),
            owner: code,
          })
        : t("inventory.common.waitingUnknown")}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Cross-module reference labels
// ---------------------------------------------------------------------------

/**
 * A name resolved from another module's read (product sku, unit serial, lot
 * code, a person). Three states, and none of them is "a uuid fragment where a
 * name belongs": the resolved text, a placeholder while the per-id index is in
 * flight, or — when the id could not be resolved at all — the short id in a
 * `<code>` chip, which reads as an identifier rather than as corrupt data.
 */
export function RefLabel({
  value,
  id,
  pending = false,
  kind,
}: {
  value: string | undefined;
  id: string | null | undefined;
  pending?: boolean;
  /** The names read's kind for `id`. When the screen's own index has settled
   * without a row for it (a later page, an archived row), the label asks
   * `GET /v1/refs` before it falls back to the short chip. */
  kind?: RefKind;
}) {
  const { t } = useTranslation();
  if (value !== undefined && value.length > 0) return <>{value}</>;
  if (id === null || id === undefined || id.length === 0) {
    return <span className="inv-muted">{t("inventory.common.none")}</span>;
  }
  if (pending) {
    return (
      <span className="ref-pending" aria-label={t("inventory.common.resolving")}>
        …
      </span>
    );
  }
  // The register's label draws the chip (and its short form) so this area
  // shows an unresolved id the same way every other screen does.
  return kind === undefined ? (
    <RegisterRefLabel id={id} />
  ) : (
    <RegisterRefLabel kind={kind} id={id} />
  );
}

/**
 * A facility named by a record that may reach outside the reader's scope: a
 * transfer's other end. The reader's own facility list names it first; the
 * names read (`GET /v1/refs`) then names one in scope that the active list
 * left out. A plant neither will name is one this reader may not see, so it
 * reads "Another plant", never its id: a uuid in a route column reads as
 * corrupt data, and its short form names nothing the reader can open.
 */
export function FacilityRef({
  id,
  name,
  pending,
}: {
  id: string;
  /** The name from the reader's facility list, when it has the row. */
  name: string | undefined;
  /** True while that list is still loading. */
  pending: boolean;
}) {
  const { t } = useTranslation();
  const client = useContext(QueryClientContext);
  if (name !== undefined && name.length > 0) return <>{name}</>;
  if (pending) {
    return (
      <span className="ref-pending" aria-label={t("inventory.common.resolving")}>
        …
      </span>
    );
  }
  // A tree with no query client (an isolated screen test) cannot ask.
  if (client === undefined) return <OtherPlant />;
  return <AskedFacilityRef id={id} />;
}

function AskedFacilityRef({ id }: { id: string }) {
  const { t } = useTranslation();
  const asked = useRefName("facility", id);
  if (asked.name !== undefined && asked.name.length > 0) return <>{asked.name}</>;
  if (asked.pending) {
    return (
      <span className="ref-pending" aria-label={t("inventory.common.resolving")}>
        …
      </span>
    );
  }
  return <OtherPlant />;
}

function OtherPlant() {
  const { t } = useTranslation();
  return (
    <span className="inv-muted" data-testid="facility-ref-other">
      {t("inventory.transfers.otherPlant")}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

/** Definition-list row used by every detail header in this area. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="inv-field">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
